import { LoadingResultList } from "@mirage/search/SearchResults/LoadingResult";
import type { SearchResult } from "@mirage/shared/search/search-result";
import { BrandedTypesDisplay } from "./BrandedTypesDisplay";
import { DebugResult } from "./DebugResult";
import { MagicFlagsConfig } from "./MagicFlagsConfig";
import { PanelHeader } from "./PanelHeader";
import styles from "./SearchPanel.module.css";

export interface PanelConfig {
  magicFlags: Record<string, string>;
  isConfigExpanded?: boolean;
}

export interface PanelResult {
  results: SearchResult[];
  loading: boolean;
  error?: string;
  latency?: number; // in milliseconds
}

interface SearchPanelProps {
  title: string;
  config?: PanelConfig;
  onConfigChange: (config: PanelConfig) => void;
  result: PanelResult;
  onRemove?: () => void;
  query: string;
  onSelectResult: ({
    result,
    panelId,
  }: {
    result: SearchResult;
    panelId: string;
  }) => void;
  isResultSelected: ({
    result,
    panelId,
  }: {
    result: SearchResult;
    panelId: string;
  }) => boolean;
  panelId: string;
}

export const SearchPanel = ({
  panelId,
  title,
  config,
  onConfigChange,
  result,
  onRemove,
  query,
  onSelectResult,
  isResultSelected,
}: SearchPanelProps) => {
  const { results, loading, error } = result;
  const searchRequestId = results[0]?.searchRequestId;

  const isConfigExpanded = config?.isConfigExpanded ?? false;
  const hasMagicFlags = config
    ? Object.keys(config.magicFlags).length > 0
    : false;

  const toggleConfigExpanded = () => {
    if (!config) return;

    onConfigChange({
      ...config,
      isConfigExpanded: !isConfigExpanded,
    });
  };

  const handleMagicFlagsChange = (magicFlags: Record<string, string>) => {
    if (!config) return;

    onConfigChange({
      ...config,
      magicFlags,
    });
  };

  return (
    <div className={styles.panel}>
      <div>
        <PanelHeader
          title={title}
          latency={result.latency}
          searchRequestId={searchRequestId}
          hasConfig={Boolean(config)}
          hasMagicFlags={hasMagicFlags}
          onToggleConfig={toggleConfigExpanded}
          onRemove={onRemove}
        />
        {isConfigExpanded && config && (
          <div className={styles.configInputs}>
            <MagicFlagsConfig
              magicFlags={config.magicFlags}
              onFlagsChange={handleMagicFlagsChange}
              query={query}
            />
          </div>
        )}

        {/* Show branded types when we have results */}
        {results.length > 0 && !loading && !error && (
          <BrandedTypesDisplay results={results} totalCount={results.length} />
        )}

        {loading && (
          <div>
            <LoadingResultList count={8} staggerMs={83} />
          </div>
        )}

        {results.length > 0 && !loading && !error && (
          <div className={styles.resultsContainer}>
            {result.results.map((searchResult, index) => (
              <DebugResult
                key={searchResult.uuid}
                result={searchResult}
                query={query}
                resultPosition={index}
                onSelect={() =>
                  onSelectResult({ result: searchResult, panelId })
                }
                isSelected={isResultSelected({
                  result: searchResult,
                  panelId,
                })}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
